import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import * as XLSX from 'xlsx';

const ExcelPreviewDialog = ({ open, onClose, fileId, tenantId, fileName: fileNameProp }) => {
  const [excelData, setExcelData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [fileName, setFileName] = useState('');

  useEffect(() => {
    const fetchExcelData = async () => {
      if (!open || !fileId || !tenantId) return;

      setLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('Authentication token not found');
        }

        // Step 1: Get presigned URL (same as Treasury Excel viewer)
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/${fileId}/presigned-url`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to get presigned URL: ${response.status}`);
        }

        const data = await response.json();
        const presignedUrl = data.url;

        if (!presignedUrl) {
          throw new Error('No URL returned from API');
        }

        // Step 2: Fetch the Excel file using presigned URL
        const fileResponse = await fetch(presignedUrl);
        if (!fileResponse.ok) {
          throw new Error('Failed to fetch Excel file');
        }

        const arrayBuffer = await fileResponse.arrayBuffer();

        // Step 3: Parse Excel file with XLSX
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        
        // Get first sheet
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
          header: 1,  // Return array of arrays
          defval: '', // Default value for empty cells
          raw: false  // Format cells as strings
        });

        if (jsonData.length === 0) {
          throw new Error('Excel file is empty');
        }

        // Step 4: Create columns from first row (headers)
        const headers = jsonData[0];
        const gridColumns = headers.map((header, index) => ({
          field: `col_${index}`,
          headerName: header || `Column ${index + 1}`,
          width: 150,
          editable: false,
        }));

        // Step 5: Create rows from remaining data
        const gridRows = jsonData.slice(1).map((row, rowIndex) => {
          const rowData = { id: rowIndex };
          row.forEach((cell, colIndex) => {
            rowData[`col_${colIndex}`] = cell || '';
          });
          return rowData;
        });

        setColumns(gridColumns);
        setRows(gridRows);
        setFileName(data.fileName || 'Excel File');
      } catch (err) {
        console.error('Error fetching Excel preview:', err);
        setError(err.message || 'Failed to load Excel preview');
      } finally {
        setLoading(false);
      }
    };

    fetchExcelData();
  }, [open, fileId, tenantId]);

  const handleClose = () => {
    setRows([]);
    setColumns([]);
    setExcelData(null);
    setError(null);
    setFileName('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 1.5,
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {fileNameProp || fileName || 'Excel File'}
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ 
        p: 0, 
        display: 'flex', 
        flexDirection: 'column',
        '&.MuiDialogContent-root': {
          padding: 0,
        }
      }}>
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%' 
          }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading Excel file...</Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        ) : rows.length > 0 ? (
          <Box sx={{ p: 2, width: '100%', height: '100%', boxSizing: 'border-box' }}>
            <DataGridPro
              rows={rows}
              columns={columns}
              density="compact"
              disableRowSelectionOnClick
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 50 },
                },
              }}
              pageSizeOptions={[25, 50, 100]}
              disableColumnMenu
              disableDensitySelector
              autoHeight={false}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                '& .MuiDataGrid-cell': {
                  fontSize: '0.875rem',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: 'background.paper',
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                },
                '& .MuiDataGrid-virtualScroller': {
                  overflowX: 'auto',
                  minHeight: '200px',
                },
                '& .MuiDataGrid-main': {
                  margin: 0,
                  width: '100%',
                }
              }}
            />
          </Box>
        ) : (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%' 
          }}>
            <Typography color="text.secondary">
              No data to display
            </Typography>
          </Box>
        )}
      </DialogContent>

    </Dialog>
  );
};

export default ExcelPreviewDialog;
